import { useQuery } from "@apollo/client";
import { Launch } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton, SvgIcon, SxProps, Theme, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";

import {
  DeleteResponseScriptGroupMutation,
  DeleteResponseScriptGroupMutationVariables,
  DeleteResponseScriptMutation,
  DeleteResponseScriptMutationVariables,
  EditResponseScriptGroupInput,
  EditResponseScriptGroupMutation,
  EditResponseScriptGroupMutationVariables,
  EditResponseScriptInput,
  EditResponseScriptMutation,
  EditResponseScriptMutationVariables,
  FieldsForResponseScriptFragment,
  FieldsForResponseScriptGroupFragment,
  ListTagsQuery,
  ListTagsQueryVariables,
  ReorderDirection,
  ReorderResponseScriptGroupMutation,
  ReorderResponseScriptGroupMutationVariables,
  ReorderResponseScriptMembershipMutation,
  ReorderResponseScriptMembershipMutationVariables,
  ResponseScript,
  ResponseScriptGroup,
  TagSortBy,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import ExpandButton from "~frontend/components/ExpandButton";
import Loading from "~frontend/components/Loading";
import TagChip from "~frontend/components/TagChip";
import {
  DELETE_RESPONSE_SCRIPT,
  DELETE_RESPONSE_SCRIPT_GROUP,
  EDIT_RESPONSE_SCRIPT,
  EDIT_RESPONSE_SCRIPT_GROUP,
  REORDER_RESPONSE_SCRIPT_GROUP,
  REORDER_RESPONSE_SCRIPT_MEMBERSHIP,
} from "~frontend/graphql/scripts";
import { LIST_TAGS } from "~frontend/graphql/tags";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { CampaignForEditor } from "~frontend/routes/subaccounts/campaignEditor/types";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import MediaAttachment from "../MediaAttachment";
import EditGroupDialog from "./EditGroupDialog";
import EditScriptDialog from "./EditScriptDialog";
import {
  EditButtons,
  ReorderAndTitleContainer,
  ReorderButtonsContainer,
  ScriptButtons,
  ScriptContent,
  ScriptGroup,
  ScriptGroupButtons,
  ScriptGroupHeading,
  ScriptGroupWrapper,
  ScriptKeywords,
  ScriptResponse,
  ScriptResponses,
  ScriptResponseScript,
  ScriptResponseTitle,
  ScriptTags,
} from "./styles";

const gsheetLink = (sheetId: string): string =>
  `https://docs.google.com/spreadsheets/d/${sheetId}/edit?gid=0`;

const DeleteLoading = () => (
  <Loading
    size={16}
    style={{
      marginTop: 12,
      marginBottom: 12,
      marginRight: 13,
      marginLeft: 10,
    }}
  />
);

const ReorderButtons = ({
  loading,
  onUp,
  onDown,
}: {
  loading: boolean;
  onUp?: () => void;
  onDown?: () => void;
}) => {
  const theme = useTheme();
  return (
    <ReorderButtonsContainer>
      {loading ? (
        <Loading
          size={16}
          style={{
            marginTop: 12,
            marginBottom: 12,
            marginRight: theme.spacing(1),
          }}
        />
      ) : (
        <>
          <IconButton disabled={!onUp} onClick={onUp} size="small">
            <SvgIcon
              width="17"
              height="9"
              viewBox="0 0 17 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8.5 0.100097L17 8.6001L0 8.6001L8.5 0.100097Z" />
            </SvgIcon>
          </IconButton>
          <IconButton disabled={!onDown} onClick={onDown} size="small">
            <SvgIcon
              width="17"
              height="10"
              viewBox="0 0 17 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8.5 9.2002L0 0.700195H17L8.5 9.2002Z" />
            </SvgIcon>
          </IconButton>
        </>
      )}
    </ReorderButtonsContainer>
  );
};
export function ResponseScriptList({
  groups,
  campaign,
  loading,
  sx = {},
}: {
  groups: FieldsForResponseScriptGroupFragment[];
  campaign?: CampaignForEditor;
  loading?: boolean;
  sectionSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const [groupHidden, setGroupHidden] = useState({} as Record<string, boolean>);
  const [groupsReordering, setGroupsReordering] = useState(
    {} as Record<string, boolean>
  );
  const [groupsDeleting, setGroupsDeleting] = useState(
    {} as Record<string, boolean>
  );
  const [scriptsDeleting, setScriptsDeleting] = useState(
    {} as Record<string, boolean>
  );

  const refetchQueries = campaign ? ["getCampaign"] : ["ListResponseScripts"];

  const [groupToEdit, setGroupToEdit] = useState<ResponseScriptGroup | null>(
    null
  );
  const [editGroup] = useMutation<
    EditResponseScriptGroupMutation,
    EditResponseScriptGroupMutationVariables
  >(EDIT_RESPONSE_SCRIPT_GROUP, {
    onErrorSnackbarMessage: "Error updating group",
    refetchQueries,
    awaitRefetchQueries: true,
  });

  async function handleEditGroup(values: EditResponseScriptGroupInput) {
    const { errors } = await editGroup({
      variables: { input: values },
    });
    if (errors) {
      throw errors;
    } else {
      setGroupToEdit(null);
    }
  }

  const [deleteGroup] = useMutation<
    DeleteResponseScriptGroupMutation,
    DeleteResponseScriptGroupMutationVariables
  >(DELETE_RESPONSE_SCRIPT_GROUP, {
    onErrorSnackbarMessage: "Error deleting response script group",
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const confirm = useConfirm();

  const handleDeleteGroup = async (
    group: FieldsForResponseScriptGroupFragment
  ) => {
    const { confirmed } = await confirm({
      title: "Delete group",
      description: `Are you sure you want to delete the "${group.title}" response group?`,
    });
    if (!confirmed) {
      return;
    }

    setGroupsDeleting({ ...groupsDeleting, [group.id]: true });
    const { errors } = await deleteGroup({
      variables: { input: { groupId: group.id } },
    });
    setGroupsDeleting(_.omit(groupsDeleting, group.id));
    if (errors) {
      throw errors;
    }
  };

  const [scriptToEdit, setScriptToEdit] = useState<ResponseScript | null>(null);
  const [editScript] = useMutation<
    EditResponseScriptMutation,
    EditResponseScriptMutationVariables
  >(EDIT_RESPONSE_SCRIPT, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  async function handleEditScript(values: EditResponseScriptInput) {
    const { errors } = await editScript({
      variables: {
        input: {
          scriptId: values.scriptId,
          title: values.title,
          script: values.script,
          mediaUploadId: values.mediaUploadId,
          groupIds: values.groupIds,
          tagIds: values.tagIds,
          keywordIdsToDelete: values.keywordIdsToDelete,
          keywordsToAdd: values.keywordsToAdd,
        },
      },
    });
    if (errors) {
      throw errors;
    } else {
      setScriptToEdit(null);
    }
  }

  const [deleteScript] = useMutation<
    DeleteResponseScriptMutation,
    DeleteResponseScriptMutationVariables
  >(DELETE_RESPONSE_SCRIPT, {
    onErrorSnackbarMessage: "Error deleting response script",
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const handleDeleteScript = async (
    script: FieldsForResponseScriptFragment
  ) => {
    const { confirmed } = await confirm({
      title: "Delete script",
      description: `Are you sure you want to delete the "${script.title}" script response?`,
    });
    if (!confirmed) {
      return;
    }

    setScriptsDeleting({ ...scriptsDeleting, [script.id]: true });
    const { errors } = await deleteScript({
      variables: { input: { scriptId: script.id } },
    });
    setScriptsDeleting(_.omit(scriptsDeleting, script.id));
    if (errors) {
      throw errors;
    }
  };

  const [reorderGroup, { loading: groupsReloading }] = useMutation<
    ReorderResponseScriptGroupMutation,
    ReorderResponseScriptGroupMutationVariables
  >(REORDER_RESPONSE_SCRIPT_GROUP, {
    onErrorSnackbarMessage: "Error reordering group",
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const moveGroup = async (
    groupId: string,
    reorderDirection: ReorderDirection
  ) => {
    const { errors } = await reorderGroup({
      variables: { input: { groupId, reorderDirection } },
    });
    if (errors) {
      throw errors;
    }
  };

  const moveGroupUp = (groupId: string) =>
    moveGroup(groupId, ReorderDirection.Up);

  const moveGroupDown = (groupId: string) =>
    moveGroup(groupId, ReorderDirection.Down);

  const [reorderScript] = useMutation<
    ReorderResponseScriptMembershipMutation,
    ReorderResponseScriptMembershipMutationVariables
  >(REORDER_RESPONSE_SCRIPT_MEMBERSHIP, {
    onErrorSnackbarMessage: "Error reordering response script",
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const moveScript = async (
    groupId: string,
    scriptGroupMembershipId: string,
    reorderDirection: ReorderDirection
  ) => {
    setGroupsReordering({ ...groupsReordering, [groupId]: true });
    const { errors } = await reorderScript({
      variables: { input: { scriptGroupMembershipId, reorderDirection } },
    });
    setGroupsReordering(_.omit(groupsReordering, groupId));
    if (errors) {
      throw errors;
    }
  };

  const moveScriptUp = (groupId: string, membershipId: string) =>
    moveScript(groupId, membershipId, ReorderDirection.Up);

  const moveScriptDown = (groupId: string, membershipId: string) =>
    moveScript(groupId, membershipId, ReorderDirection.Down);

  const listTagsResult = useQuery<ListTagsQuery, ListTagsQueryVariables>(
    LIST_TAGS,
    {
      variables: {
        input: {
          sort: TagSortBy.NameAsc,
          subaccountId: subaccount.id,
        },
      },
      fetchPolicy: "network-only",
    }
  );

  if (listTagsResult.error) {
    return <ErrorCard errors={[listTagsResult.error]} />;
  }

  if (loading || listTagsResult.loading) {
    return <Loading />;
  }

  const subaccountTags = listTagsResult.data?.listTags?.tags || [];

  return (
    <>
      {groupToEdit && (
        <EditGroupDialog
          open={!!groupToEdit}
          valuesToEdit={groupToEdit}
          onClose={() => {
            setGroupToEdit(null);
          }}
          onSubmit={handleEditGroup}
          allowSettingSubaccountDefault={!campaign}
        />
      )}
      {scriptToEdit && (
        <EditScriptDialog
          open={!!scriptToEdit}
          valuesToEdit={scriptToEdit}
          scriptGroups={groups}
          subaccountTags={subaccountTags}
          onClose={() => {
            setScriptToEdit(null);
          }}
          onSubmit={handleEditScript}
          campaign={campaign}
        />
      )}

      <ScriptGroupWrapper>
        {groups.map((group, index) => (
          <ScriptGroup key={group.id} sx={sx}>
            <ScriptGroupHeading>
              <ReorderAndTitleContainer>
                <ReorderButtons
                  loading={groupsReloading}
                  onUp={index !== 0 ? () => moveGroupUp(group.id) : undefined}
                  onDown={
                    index !== groups.length - 1
                      ? () => moveGroupDown(group.id)
                      : undefined
                  }
                />

                <div
                  onClick={() =>
                    setGroupHidden({
                      ...groupHidden,
                      [group.id]: !groupHidden[group.id],
                    })
                  }
                >
                  {group.title}
                </div>
              </ReorderAndTitleContainer>
              <ScriptGroupButtons>
                <EditButtons>
                  <IconButton
                    onClick={() => setGroupToEdit(group as ResponseScriptGroup)}
                  >
                    <EditIcon />
                  </IconButton>
                  {groupsDeleting[group.id] ? (
                    <DeleteLoading />
                  ) : (
                    <IconButton onClick={() => handleDeleteGroup(group)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                  <ExpandButton
                    onClick={() =>
                      setGroupHidden({
                        ...groupHidden,
                        [group.id]: !groupHidden[group.id],
                      })
                    }
                    open={!groupHidden[group.id]}
                    sx={{ fontSize: "30px" }}
                    svgSx={{
                      color: palette.navy,
                    }}
                  />
                </EditButtons>
              </ScriptGroupButtons>
            </ScriptGroupHeading>
            {!groupHidden[group.id] && !!group.memberships.length && (
              <ScriptResponses>
                {group.memberships.map(({ script, id }, index) => (
                  <ScriptResponse key={script.id}>
                    <ScriptContent>
                      <ScriptResponseTitle>
                        <ReorderAndTitleContainer>
                          <ReorderButtons
                            loading={groupsReordering[group.id]}
                            onUp={
                              index !== 0
                                ? () => moveScriptUp(group.id, id)
                                : undefined
                            }
                            onDown={
                              index !== group.memberships.length - 1
                                ? () => moveScriptDown(group.id, id)
                                : undefined
                            }
                          />
                          {script.title}
                        </ReorderAndTitleContainer>
                      </ScriptResponseTitle>
                      <ScriptResponseScript>
                        {script.mediaUpload?.publicGetUrl && (
                          <MediaAttachment
                            src={script.mediaUpload.publicGetUrl}
                            contentType={script.mediaUpload.contentType}
                          />
                        )}
                        {script.script}
                        {script.tags.length ? (
                          <ScriptTags>
                            {script.tags.map(tag => (
                              <TagChip key={tag.id} label={tag.name} />
                            ))}
                          </ScriptTags>
                        ) : null}
                        <ScriptKeywords keywords={script.keywords} />
                      </ScriptResponseScript>
                    </ScriptContent>
                    <ScriptButtons>
                      {script?.sourceSheetId && (
                        <Tooltip title="View import source">
                          <IconButton
                            href={gsheetLink(script.sourceSheetId)}
                            target="_blank"
                          >
                            <Launch />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        onClick={() =>
                          setScriptToEdit(script as ResponseScript)
                        }
                      >
                        <EditIcon />
                      </IconButton>
                      {scriptsDeleting[script.id] ? (
                        <DeleteLoading />
                      ) : (
                        <IconButton onClick={() => handleDeleteScript(script)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </ScriptButtons>
                  </ScriptResponse>
                ))}
              </ScriptResponses>
            )}
          </ScriptGroup>
        ))}
      </ScriptGroupWrapper>
    </>
  );
}
